import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid, Box, CircularProgress, FormControl, Select, MenuItem, OutlinedInput, Typography,
} from '@mui/material';
import UserInfoTable from './UserInfoTable';
import { useApiClient, convertAxiosErrorToUserFacingMessage } from '../../context/ApiClientContext';
import basicServiceHandler from '../../services/basicServiceHandler';
import Toast, { useToast } from '../../components/useToast';
import useAuth from '../../hooks/useAuth';
import CustomAppBar from '../../content/Appbar/CustomAppBar';

function ManageSiteUsers() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(false);

  const apiClient = useApiClient();
  const { accessibleSiteIds } = useAuth();
  const {
    toastState,
    toastSetSuccessNotification,
    toastSetErrorNotification,
    toastOnClose,
  } = useToast();

  const selectedSiteName = sites.find((s) => s.id === selectedSiteId)?.name || '';

  const setSuccessMessage = (message) => {
    toastSetSuccessNotification(message);
  };

  const setErrorMessage = (message) => {
    toastSetErrorNotification(message);
  };

  const fetchSites = useCallback(async () => {
    if (!accessibleSiteIds || accessibleSiteIds.length === 0) {
      return;
    }

    try {
      const sitePromises = accessibleSiteIds.map((siteId) => apiClient.getSite(siteId));
      const siteResponses = await Promise.all(sitePromises);

      const siteData = siteResponses.map((response) => ({
        id: response.data.id,
        name: response.data.name,
      }));

      setSites(siteData);

      // Set first site as default selection
      if (siteData.length > 0) {
        setSelectedSiteId(siteData[0].id);
      }
    } catch (error) {
      setSites([]);
      setErrorMessage('Failed to fetch sites');
    }
  }, [accessibleSiteIds]);

  // Fetch users for selected site
  const fetchUsers = useCallback(() => {
    if (!selectedSiteId) return;

    setLoading(true);
    basicServiceHandler(
      async () => {
        const result = await apiClient.getUsersBySiteId(selectedSiteId);
        return result;
      },
      (result) => {
        const formattedUsers = result.data.map((user) => ({
          username: user.username,
          roles: user.roles,
          phoneNumber: user.phoneNumber,
          mfaMethod: user.mfaMethod,
        }));

        setUsers(formattedUsers);
        setLoading(false);
      },
      () => {
        setUsers([]);
        setErrorMessage('Failed to fetch users');
        setLoading(false);
      },
    );
  }, [selectedSiteId, apiClient]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Load users when site selection changes
  useEffect(() => {
    if (selectedSiteId) {
      fetchUsers();
    }
  }, [selectedSiteId, fetchUsers]);

  const handleSiteChange = (siteId) => {
    setSelectedSiteId(siteId);
  };

  return (
    <Box sx={{ height: '100vh' }}>
      <Grid container>
        <Grid sx={{ width: 1 }}>
          <CustomAppBar
            name="Site Users Management"
          />
        </Grid>
        <Grid sx={{ width: 1, padding: '0 30px' }}>
          {!loading && sites.length === 0 && (
            <Box>
              <Typography>You do not have access to any sites.</Typography>
            </Box>
          )}
          {sites.length > 0 && (
            <Box sx={{
              mb: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}
            >
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={selectedSiteId}
                  onChange={(e) => handleSiteChange(e.target.value)}
                  displayEmpty
                  input={<OutlinedInput />}
                  sx={{
                    maxHeight: '44px',
                    minWidth: '240px',
                  }}
                  renderValue={(value) => {
                    if (!value) {
                      return <span style={{ color: '#666' }}>Select a Site</span>;
                    }
                    const foundSite = sites.find((site) => site.id === value);
                    return foundSite ? foundSite.name : value;
                  }}
                >
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {loading ? (
            <Box sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px',
            }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <UserInfoTable
              data={users}
              siteId={selectedSiteId}
              siteName={selectedSiteName}
              fetchUsers={fetchUsers}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={(error) => {
                const errorMessage = typeof error === 'string' ? error : convertAxiosErrorToUserFacingMessage(error);
                setErrorMessage(errorMessage);
              }}
            />
          )}
        </Grid>
      </Grid>
      <Toast
        open={toastState.open}
        severity={toastState.severity}
        text={toastState.text}
        onClose={toastOnClose}
      />
    </Box>
  );
}

export default ManageSiteUsers;
